/**
 * Canvas-based renderer for the free fall simulation
 * Handles visualization of towers, balls, and position indicators
 */

import type { SimulationState, SimulationParams, Ball } from '../types';
import towerImageSrc from '../assets/tower.png';

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private showGuides: boolean = true;

  // Viewport parameters (in pixels)
  private width: number = 0;
  private height: number = 0;

  // Tower image
  private towerImage: HTMLImageElement;
  private towerImageLoaded: boolean = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2d context');
    this.ctx = ctx;

    // Load tower image
    this.towerImage = new Image();
    this.towerImage.onload = () => {
      this.towerImageLoaded = true;
      // Trigger a re-render when image loads
      this.canvas.dispatchEvent(new CustomEvent('canvasResized'));
    };
    this.towerImage.src = towerImageSrc;

    this.setupResizeHandler();
    // Initial canvas setup
    this.setupCanvas();
  }

  private setupResizeHandler(): void {
    const resizeObserver = new ResizeObserver(() => {
      // Update canvas resolution on resize and trigger a re-render
      this.setupCanvas();
      // Notify that a render is needed via a custom event
      this.canvas.dispatchEvent(new CustomEvent('canvasResized'));
    });

    if (this.canvas.parentElement) {
      resizeObserver.observe(this.canvas.parentElement);
    }
  }

  public setShowGuides(show: boolean): void {
    this.showGuides = show;
  }

  private calculateRequiredHeight(params: SimulationParams, canvasWidth: number): number {
    const PADDING = 0;
    const BOTTOM_MARGIN = 20; // Space for ground (reduced from 40)
    const TOP_MARGIN = 80; // Space for sky (increased from 40)

    // Calculate scale based on available width and tower dimensions
    // Tower image is 3x higher than wide, so tower width in meters = towerHeight / 3
    const availableWidth = canvasWidth - 2 * PADDING;

    // We need to fit: left tower, space between towers, right tower, plus margins
    // Each tower width in meters = towerHeight / 3
    const towerWidthMeters = params.towerHeight / 3;
    const totalTowerSpan = Math.abs(params.rightTowerX - params.leftTowerX) + 2 * towerWidthMeters; // Add margins
    const scale = availableWidth / totalTowerSpan;

    // Calculate tower height in pixels
    const towerHeightPixels = params.towerHeight * scale;

    // Total height needed
    const totalHeight = TOP_MARGIN + towerHeightPixels + BOTTOM_MARGIN;

    return totalHeight;
  }

  public resize(params?: SimulationParams): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;

    const rect = parent.getBoundingClientRect();

    if (params) {
      // Calculate required height based on content
      const calculatedHeight = this.calculateRequiredHeight(params, rect.width);
      const height = Math.max(300, Math.min(800, calculatedHeight));
      parent.style.height = `${height}px`;
    }

    this.setupCanvas();
  }

  private setupCanvas(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();

    // Set canvas internal resolution (high DPI)
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    // Reset transformation and scale context to match DPR
    this.ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
    this.ctx.scale(dpr, dpr);

    // Store logical dimensions
    this.width = this.canvas.width;
    this.height = this.canvas.height;
  }

  private getLogicalWidth(): number {
    const dpr = window.devicePixelRatio || 1;
    return this.width / dpr;
  }

  private getLogicalHeight(): number {
    const dpr = window.devicePixelRatio || 1;
    return this.height / dpr;
  }

  private toScreenX(worldX: number, scale: number, offsetX: number): number {
    return offsetX + worldX * scale;
  }

  private toScreenY(worldY: number, scale: number, offsetY: number): number {
    return offsetY - worldY * scale;
  }

  public render(state: SimulationState, params: SimulationParams): void {
    // Update canvas resolution before rendering
    this.setupCanvas();

    const canvasWidth = this.getLogicalWidth();
    const canvasHeight = this.getLogicalHeight();

    // Calculate scale and offsets dynamically
    const PADDING = 10;
    const availableWidth = canvasWidth - 2 * PADDING;

    // Tower image is 3x higher than wide, so tower width in meters = towerHeight / 3
    const towerWidthMeters = params.towerHeight / 3;
    const totalTowerSpan = Math.abs(params.rightTowerX - params.leftTowerX) + 2 * towerWidthMeters + 2;
    const scale = availableWidth / totalTowerSpan;

    const offsetX = canvasWidth / 2;
    const offsetY = canvasHeight - 40; // 40px from bottom for ground (reduced to show less grass)

    // Clear canvas
    this.ctx.fillStyle = '#a7eaec'; // Sky blue background
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw ground
    this.drawGround(params.groundLevel, scale, offsetX, offsetY, canvasWidth, canvasHeight);

    // Draw towers
    this.drawTower(params.leftTowerX, params.towerHeight, params.groundLevel, scale, offsetX, offsetY);
    this.drawTower(params.rightTowerX, params.towerHeight, params.groundLevel, scale, offsetX, offsetY);

    // Draw launcher (only on left tower)
    this.drawLauncher(params.leftTowerX + 3, params.towerHeight, params.leftLaunch.angle, scale, offsetX, offsetY);

    // Draw trajectories (before guides and balls so they appear behind)
    this.drawTrajectory(state.leftBall, scale, offsetX, offsetY);
    this.drawTrajectory(state.rightBall, scale, offsetX, offsetY);

    // Draw position guides and indicators
    if (this.showGuides) {
      if (state.leftBall.active) {
        this.drawGuides(state.leftBall, params.groundLevel, scale, offsetX, offsetY, canvasWidth, canvasHeight);
        this.drawPositionIndicators(state.leftBall, 'left', scale, offsetX, offsetY, canvasWidth, canvasHeight);
      }
      if (state.rightBall.active) {
        this.drawGuides(state.rightBall, params.groundLevel, scale, offsetX, offsetY, canvasWidth, canvasHeight);
        this.drawPositionIndicators(state.rightBall, 'right', scale, offsetX, offsetY, canvasWidth, canvasHeight);
      }
    }

    // Draw balls
    this.drawBall(state.leftBall, scale, offsetX, offsetY);
    this.drawBall(state.rightBall, scale, offsetX, offsetY);
  }

  private drawGround(groundLevel: number, scale: number, _offsetX: number, offsetY: number, canvasWidth: number, canvasHeight: number): void {
    const groundY = this.toScreenY(groundLevel, scale, offsetY);
    this.ctx.fillStyle = '#06632a'; // Green ground
    this.ctx.fillRect(0, groundY, canvasWidth, canvasHeight - groundY);
  }

  private drawTower(x: number, height: number, groundLevel: number, scale: number, offsetX: number, offsetY: number): void {
    const screenX = this.toScreenX(x, scale, offsetX);
    const topY = this.toScreenY(height, scale, offsetY);
    const bottomY = this.toScreenY(groundLevel, scale, offsetY);
    const towerHeightPixels = bottomY - topY;

    // Tower image is 3x higher than wide, so width = height / 3
    const towerWidth = towerHeightPixels / 3;

    if (this.towerImageLoaded) {
      // Draw the tower image, preserving 3:1 aspect ratio
      this.ctx.drawImage(
        this.towerImage,
        screenX - towerWidth / 2,
        topY,
        towerWidth,
        towerHeightPixels
      );
    } else {
      // Fallback to gray rectangle if image not loaded
      this.ctx.fillStyle = '#686868';
      this.ctx.fillRect(
        screenX - towerWidth / 2,
        topY,
        towerWidth,
        towerHeightPixels
      );
    }
  }

  private drawLauncher(x: number, height: number, angle: number, scale: number, offsetX: number, offsetY: number): void {
    const screenX = this.toScreenX(x, scale, offsetX);
    const screenY = this.toScreenY(height, scale, offsetY);
    const launcherWidth = 1.5 * scale;
    const launcherHeight = 0.6 * scale;

    this.ctx.save();
    this.ctx.translate(screenX, screenY);
    this.ctx.rotate((-angle * Math.PI) / 180); // Negative because canvas y is inverted

    this.ctx.fillStyle = '#222222'; // Dark launcher
    // Draw from rotation point to the right (removed left half)
    this.ctx.fillRect(
      0,
      -launcherHeight / 2,
      launcherWidth,
      launcherHeight
    );

    this.ctx.restore();
  }

  private drawBall(ball: Ball, scale: number, offsetX: number, offsetY: number): void {
    const screenX = this.toScreenX(ball.position.x, scale, offsetX);
    const screenY = this.toScreenY(ball.position.y, scale, offsetY);
    const radius = ball.radius * scale;

    this.ctx.fillStyle = ball.color;
    this.ctx.beginPath();
    this.ctx.arc(screenX, screenY, radius, 0, 2 * Math.PI);
    this.ctx.fill();
  }

  private drawTrajectory(ball: Ball, scale: number, offsetX: number, offsetY: number): void {
    if (ball.trajectory.length < 2) return;

    this.ctx.strokeStyle = ball.color;
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([]);

    this.ctx.beginPath();
    const firstPoint = ball.trajectory[0];
    this.ctx.moveTo(this.toScreenX(firstPoint.x, scale, offsetX), this.toScreenY(firstPoint.y, scale, offsetY));

    for (let i = 1; i < ball.trajectory.length; i++) {
      const point = ball.trajectory[i];
      this.ctx.lineTo(this.toScreenX(point.x, scale, offsetX), this.toScreenY(point.y, scale, offsetY));
    }

    this.ctx.stroke();
  }

  private drawGuides(ball: Ball, groundLevel: number, scale: number, offsetX: number, offsetY: number, _canvasWidth: number, _canvasHeight: number): void {
    const screenX = this.toScreenX(ball.position.x, scale, offsetX);
    const screenY = this.toScreenY(ball.position.y, scale, offsetY);
    const groundY = this.toScreenY(groundLevel, scale, offsetY);

    this.ctx.strokeStyle = ball.color;
    this.ctx.setLineDash([5, 5]);
    this.ctx.lineWidth = 1.5;

    // Vertical line from ball to ground
    this.ctx.beginPath();
    this.ctx.moveTo(screenX, screenY);
    this.ctx.lineTo(screenX, groundY);
    this.ctx.stroke();

    // Horizontal line from ball to left edge
    this.ctx.beginPath();
    this.ctx.moveTo(screenX, screenY);
    this.ctx.lineTo(0, screenY);
    this.ctx.stroke();

    this.ctx.setLineDash([]);
  }

  private drawPositionIndicators(ball: Ball, side: 'left' | 'right', scale: number, offsetX: number, offsetY: number, canvasWidth: number, _canvasHeight: number): void {
    const screenX = this.toScreenX(ball.position.x, scale, offsetX);
    const screenY = this.toScreenY(ball.position.y, scale, offsetY);
    const groundY = this.toScreenY(0, scale, offsetY);

    this.ctx.fillStyle = ball.color;
    this.ctx.strokeStyle = ball.color;
    this.ctx.lineWidth = 1;

    // Bottom indicator (horizontal position on ground)
    const bottomX = screenX;
    const bottomY = groundY + 20;
    this.drawTriangle(bottomX, bottomY, 10, 'up');

    // Side indicator (vertical position)
    const sideX = side === 'left' ? 20 : canvasWidth - 20;
    const sideY = screenY;
    this.drawTriangle(sideX, sideY, 10, side === 'left' ? 'right' : 'left');

    // Top horizontal indicator
    const topX = side === 'left' ? screenX : canvasWidth - screenX + canvasWidth / 2;
    const topY = 20;

    // Draw horizontal position indicator at top
    if (side === 'left') {
      this.drawTriangle(topX, topY, 10, 'down');
    } else {
      const rightX = canvasWidth - (this.toScreenX(0, scale, offsetX) - screenX);
      this.drawTriangle(rightX, topY, 10, 'down');
    }
  }

  private drawTriangle(
    x: number,
    y: number,
    size: number,
    direction: 'up' | 'down' | 'left' | 'right'
  ): void {
    this.ctx.beginPath();

    switch (direction) {
      case 'up':
        this.ctx.moveTo(x, y - size);
        this.ctx.lineTo(x - size, y + size);
        this.ctx.lineTo(x + size, y + size);
        break;
      case 'down':
        this.ctx.moveTo(x, y + size);
        this.ctx.lineTo(x - size, y - size);
        this.ctx.lineTo(x + size, y - size);
        break;
      case 'left':
        this.ctx.moveTo(x - size, y);
        this.ctx.lineTo(x + size, y - size);
        this.ctx.lineTo(x + size, y + size);
        break;
      case 'right':
        this.ctx.moveTo(x + size, y);
        this.ctx.lineTo(x - size, y - size);
        this.ctx.lineTo(x - size, y + size);
        break;
    }

    this.ctx.closePath();
    this.ctx.fill();
  }
}
